import { fmtNum, fmtPct } from '../utils/fmt';
import { useMemo } from 'react';
import InfoTooltip from './InfoTooltip';
import SourceDot, { mergeMeta } from './SourceDot';
import { useCoinRSI } from '../utils/rsi';
import DataSourceDown from './DataSourceDown';
import './StrategiesTab.css';

// ── Condition checker ─────────────────────────────────────────────────────────
// Every label DESCRIBES which conditions hold right now. None of them tells
// the user to open a position or start a bot.

const MET     = (t) => ({ status: 'green',  label: `A feltételek teljesülnek: ${t}` });
const PARTIAL = (t) => ({ status: 'yellow', label: `Részben teljesülnek: ${t}` });
const NOTMET  = (t) => ({ status: 'red',    label: `Most nem teljesülnek: ${t}` });
const NODATA  = (t) => ({ status: 'na',     label: `Adat nem elérhető${t ? ` — ${t}` : ''}` });

function checkStrategy(id, { coins, rsi, fundingData, lsData }) {
  const btc = coins?.find(c => c.id === 'bitcoin');
  const btc7d = btc?.price_change_percentage_7d_in_currency ?? 0;

  // Real RSI14 (1D, closed candles) from Bybit — see utils/rsi.js
  const btcRSI = rsi.btc;
  const { oversold, overbought, measured } = rsi;

  // Direction of the 1h / 24h / 7d price changes (not an indicator)
  function trendScore(coin) {
    const c1h  = coin?.price_change_percentage_1h_in_currency ?? 0;
    const c24h = coin?.price_change_percentage_24h ?? 0;
    const c7d  = coin?.price_change_percentage_7d_in_currency ?? 0;
    return Math.sign(c1h) + Math.sign(c24h) * 2 + Math.sign(c7d) * 3;
  }

  const btcTrend = trendScore(btc);
  const isSideways = Math.abs(btcTrend) <= 1;
  const isRising   = btcTrend >= 4;

  // Funding (Bybit + Binance average, 8h)
  const btcFunding = fundingData?.BTC?.avgRate ?? null;
  const fundPct = btcFunding != null ? btcFunding * 100 : null;

  // Long/short account ratio (Bybit, 1h)
  const btcLS = lsData?.BTCUSDT?.ratio ?? null;

  switch (id) {
    case 'rsi_bounce':
      if (!measured) return NODATA('RSI (Bybit)');
      if (oversold >= 5) return MET(`${oversold} coin túladott (napi RSI < 30)`);
      if (oversold >= 2) return PARTIAL(`${oversold} coin túladott (napi RSI < 30)`);
      return NOTMET(`${oversold} coin túladott a top ${measured} közül (napi RSI < 30)`);

    case 'macd_cross':
      if (btcRSI == null) return NODATA('BTC RSI');
      if (isRising && btcRSI > 50 && btcRSI < 70)
        return MET(`emelkedő BTC-árirány, napi RSI ${fmtNum(btcRSI, 0)} (50–70 között). A MACD-keresztezést a charton ellenőrizd.`);
      if (btcTrend >= 2)
        return PARTIAL(`mérsékelten emelkedő BTC-árirány, napi RSI ${fmtNum(btcRSI, 0)}`);
      return NOTMET(`a BTC-árirány nem emelkedő, napi RSI ${fmtNum(btcRSI, 0)}`);

    case 'trend_ema':
      if (isRising && btc7d > 5)
        return MET(`a BTC erősen emelkedik (7d: ${fmtPct(btc7d, 1)})`);
      if (btcTrend >= 2)
        return PARTIAL(`enyhén emelkedő BTC-árirány (7d: ${fmtPct(btc7d, 1)})`);
      return NOTMET(`nincs egyértelmű emelkedő BTC-árirány (7d: ${fmtPct(btc7d, 1)})`);

    case 'bollinger':
      if (!measured) return NODATA('RSI (Bybit)');
      if (overbought === 0 && oversold === 0 && isSideways)
        return MET('oldalazó BTC, egyetlen coin sem túladott vagy túlvett');
      if (isSideways)
        return PARTIAL('a BTC oldalaz; a sávok szűkülését a charton ellenőrizd');
      return NOTMET('a piac aktívan mozog');

    case 'spot_grid':
      if (btcRSI == null) return NODATA('BTC RSI');
      if (isSideways && btcRSI >= 40 && btcRSI <= 60)
        return MET(`oldalazó BTC, napi RSI ${fmtNum(btcRSI, 0)} (40–60 között)`);
      if (isSideways)
        return PARTIAL(`a BTC oldalaz, de a napi RSI ${fmtNum(btcRSI, 0)} a 40–60 sávon kívül van`);
      return NOTMET('a BTC nem oldalaz (trendel)');

    case 'infinity_grid':
      if (isRising && btc7d > 3)
        return MET(`emelkedő BTC-árirány (7d: ${fmtPct(btc7d, 1)})`);
      if (btcTrend >= 2)
        return PARTIAL('enyhén emelkedő BTC-árirány');
      return NOTMET('nincs emelkedő BTC-árirány');

    case 'funding_arb':
      if (fundPct == null) return NODATA('BTC funding ráta');
      if (fundPct > 0.03)
        return MET(`a BTC funding ${fmtNum(fundPct, 4)}% / 8h (0,03% felett)`);
      if (fundPct > 0.01)
        return PARTIAL(`a BTC funding ${fmtNum(fundPct, 4)}% / 8h (0,01–0,03% között)`);
      return NOTMET(`a BTC funding ${fmtNum(fundPct, 4)}% / 8h (normál szint)`);

    case 'squeeze_play':
      if (btcLS == null) return NODATA('BTC long/short arány');
      // Many longs → the LONGS are at risk of being squeezed (long squeeze),
      // many shorts → short squeeze. (The old label had this backwards.)
      if (btcLS > 2.0)
        return MET(`L/S arány ${fmtNum(btcLS, 2)}× — sok long, long squeeze kockázat`);
      if (btcLS < 0.8)
        return MET(`L/S arány ${fmtNum(btcLS, 2)}× — sok short, short squeeze kockázat`);
      if (btcLS > 1.5)
        return PARTIAL(`L/S arány ${fmtNum(btcLS, 2)}× — erősödő long túlsúly`);
      return NOTMET(`L/S arány ${fmtNum(btcLS, 2)}× — kiegyensúlyozott`);

    default:
      return NODATA();
  }
}

// ── Strategy Card ─────────────────────────────────────────────────────────────

function StrategyCard({ strategy, signal }) {
  const statusColor = signal.status === 'green' ? '#00c853'
    : signal.status === 'yellow' ? '#f5a623'
    : signal.status === 'na' ? 'var(--text3)'
    : '#ff4d4d';

  const statusBg = signal.status === 'green' ? 'rgba(0,200,83,0.08)'
    : signal.status === 'yellow' ? 'rgba(245,166,35,0.08)'
    : signal.status === 'na' ? 'rgba(136,136,136,0.08)'
    : 'rgba(255,77,77,0.06)';

  const typeBadge = {
    spot:    { bg: 'rgba(0,255,136,0.15)', color: '#00ff88', border: 'rgba(0,255,136,0.4)', label: 'Spot'     },
    grid:    { bg: 'rgba(0,180,255,0.15)', color: '#00b4ff', border: 'rgba(0,180,255,0.4)', label: 'Grid Bot' },
    futures: { bg: 'rgba(255,165,0,0.15)', color: '#ffa500', border: 'rgba(255,165,0,0.4)', label: 'Futures'  },
  }[strategy.type];

  return (
    <div className="strat-card" style={{ borderLeftColor: statusColor, '--type-color': typeBadge.color }}>
      <div className="strat-card-head">
        <div className="strat-card-title-row">
          <span className="strat-card-name">{strategy.name}</span>
          <span className="strat-type-badge" style={{ background: typeBadge.bg, color: typeBadge.color, border: `1px solid ${typeBadge.border}` }}>
            {typeBadge.label}
          </span>
          <span className="strat-difficulty">
            {'★'.repeat(strategy.difficulty)}{'☆'.repeat(3 - strategy.difficulty)}
          </span>
        </div>
        <div className="strat-card-desc">{strategy.desc}</div>
      </div>

      <div className="strat-conditions">
        <div className="strat-conditions-title">Belépési feltételek:</div>
        <div className="strat-tags">
          {strategy.conditions.map((c, i) => (
            <span key={i} className="strat-tag">{c}</span>
          ))}
        </div>
      </div>

      <div className="strat-exit">
        <span className="strat-exit-label">Kiszállás: </span>
        <span className="strat-exit-text">{strategy.exit}</span>
      </div>

      <div className="strat-signal-box" style={{ background: statusBg, borderColor: statusColor }}>
        <span className="strat-signal-dot" style={{ background: statusColor }} />
        <span className="strat-signal-text" style={{ color: statusColor }}>{signal.label}</span>
      </div>

      <div className="strat-risk">
        <span className="strat-risk-label">Kockázat: </span>
        <span className="strat-risk-text">{strategy.risk}</span>
      </div>
    </div>
  );
}

// ── Strategies data ───────────────────────────────────────────────────────────

const STRATEGIES = [
  {
    id: 'rsi_bounce',
    name: '1. RSI Visszapattanás',
    type: 'spot',
    difficulty: 1,
    desc: 'RSI 30 alá esik → túladott zóna → belépési lehetőség long irányba. Akkor erős ha nagy volume kíséri és a gyertyazárás visszafordul.',
    conditions: ['RSI < 30', 'Forgalmi kiugrás', 'Gyertya: kalapács / fordulat', 'Támasz közelében'],
    exit: 'RSI 50-60 fölé emelkedik, vagy ellenállás szintnél',
    risk: 'Alacsony — spot vétel, nincs likvidációs kockázat',
  },
  {
    id: 'macd_cross',
    name: '2. MACD Keresztezés',
    type: 'spot',
    difficulty: 2,
    desc: 'MACD vonal keresztezi a szignálvonalat alulról felfelé → vételi jel. Erősebb ha 0 vonal felett történik és az ár emelkedő trendben van.',
    conditions: ['MACD > Szignálvonal', 'Keresztezés 0 felett', '24h trend: emelkedő', 'Forgalom növekvő'],
    exit: 'MACD visszakereszti a szignálvonalat lefelé',
    risk: 'Alacsony — késői jelzés, de megbízható megerősítés',
  },
  {
    id: 'trend_ema',
    name: '3. Trendkövetés (EMA)',
    type: 'spot',
    difficulty: 2,
    desc: 'EMA 20 keresztezi az EMA 50-et felfelé → bullish trendváltás. Az ár az EMA felett tartózkodik. MTF elemzés is emelkedőt mutat.',
    conditions: ['EMA20 > EMA50', 'Ár > EMA20', 'MTF: bullish', 'Trend: emelkedő'],
    exit: 'Ár visszaesik EMA20 alá és ott is zár',
    risk: 'Közepes — trendelő piacon hatékony, oldalazásban sok hamis jelzés',
  },
  {
    id: 'bollinger',
    name: '4. Bollinger Szorítás (Squeeze)',
    type: 'spot',
    difficulty: 2,
    desc: 'A Bollinger sávok nagyon összeszűkülnek (squeeze) → nagy mozgás várható. Az ár kitör a sávból felfelé → long belépés.',
    conditions: ['BB sávok szűkülnek', 'Kitörés felső sávon', 'Forgalmi megerősítés', 'RSI 50 felett'],
    exit: 'Ár visszaesik a középső BB vonal alá',
    risk: 'Közepes — a kitörés iránya előre nem mindig jelezhető',
  },
  {
    id: 'spot_grid',
    name: '5. Spot Grid Bot — Oldalazó piac',
    type: 'grid',
    difficulty: 1,
    desc: 'Oldalazó, sávos piacra való eszköz: a grid bot a megadott sávon belül automatikusan vesz alacsonyabban és elad magasabban.',
    conditions: ['Trend: oldalazó', 'RSI 40–60 között', 'Alacsony volatilitás', 'BTC dom stabil'],
    exit: 'Ha az ár kitör a beállított sávból — azonnal leállítani!',
    risk: 'Alacsony — spot, nem kell stop loss, de kitörésnél veszteség lehetséges',
  },
  {
    id: 'infinity_grid',
    name: '6. Infinity Grid Bot — Emelkedő piac',
    type: 'grid',
    difficulty: 2,
    desc: 'Emelkedő trendben az infinity grid folyamatosan felfelé tolódik. Nincs felső határ — mindaddig profitál amíg az ár emelkedik.',
    conditions: ['Trend: emelkedő', 'MTF: bullish', 'RSI 50–70 között', 'Forgalom növekvő'],
    exit: 'Ha MTF bearish lesz, vagy 7d trend negatívba fordul',
    risk: 'Közepes — trendfordulatnál gyors leállítás kell',
  },
  {
    id: 'funding_arb',
    name: '7. Funding Rate Arbitrázs',
    type: 'futures',
    difficulty: 3,
    desc: 'Ha a funding ráta extrém magas → short pozíció nyitása a funding bevételéért, spot long hedge mellett. Piaci irány semleges.',
    conditions: ['Funding > 0.03%', 'Extrém long dominancia', 'Spot hedge megvan', 'Stabil piaci körülmény'],
    exit: 'Ha a funding ráta visszaesik normális szintre (< 0.01%)',
    risk: 'Haladó — delta-semleges pozíció kell a biztonsághoz',
  },
  {
    id: 'squeeze_play',
    name: '8. Squeeze Play',
    type: 'futures',
    difficulty: 3,
    desc: 'Extrém L/S arány esetén a túlsúlyban lévő oldal pozíciói kerülhetnek nyomás alá. Sok long → long squeeze kockázat (esésnél a longok kényszerzárása gyorsítja az esést). Sok short → short squeeze kockázat.',
    conditions: ['L/S arány > 2.0', 'OI növekvő', 'Funding extrém', 'Kulcsszint közelében'],
    exit: 'Célár elérésénél vagy ha a squeeze dinamika leáll',
    risk: 'Magas — időzítés kritikus, csak tapasztalt kereskedőknek',
  },
];

// ── Main Export ───────────────────────────────────────────────────────────────

const RSI_TOP_N = 100;

export default function StrategiesTab({ coins, fundingData, lsData, marketsMeta, derivMeta, previewLimit }) {
  // FREE preview: only the first cards are readable → RSI for the top few coins only.
  const top = useMemo(() => (coins ?? []).slice(0, previewLimit || RSI_TOP_N), [coins, previewLimit]);
  const { rows: rsiRows, meta: rsiMeta, tickersFailed, failed, retry } = useCoinRSI(top, 'D');

  const rsi = useMemo(() => {
    const vals = Object.values(rsiRows).map(r => r.rsi).filter(v => v != null);
    return {
      btc: rsiRows.bitcoin?.rsi ?? null,
      measured: vals.length,
      oversold: vals.filter(v => v < 30).length,
      overbought: vals.filter(v => v > 70).length,
    };
  }, [rsiRows]);

  const signals = useMemo(() =>
    STRATEGIES.reduce((acc, s) => {
      acc[s.id] = checkStrategy(s.id, { coins, rsi, fundingData, lsData });
      return acc;
    }, {}),
  [coins, rsi, fundingData, lsData]);

  const activeCount = Object.values(signals).filter(s => s.status === 'green').length;
  const watchCount  = Object.values(signals).filter(s => s.status === 'yellow').length;

  const spotStrategies    = STRATEGIES.filter(s => s.type === 'spot');
  const gridStrategies    = STRATEGIES.filter(s => s.type === 'grid');
  const futuresStrategies = STRATEGIES.filter(s => s.type === 'futures');

  return (
    <div className="strategies-tab">

      <div className="strat-header">
        <div className="strat-title">
          📊 Kereskedési Stratégiák
          <InfoTooltip text="A kártyák azt mutatják, hogy a leírt piaci feltételek most teljesülnek-e: valódi napi RSI (Bybit), árváltozás (CoinGecko), funding ráta (Bybit + Binance) és long/short arány (Bybit). Ez nem pénzügyi tanácsadás és nem kereskedési jelzés — mindig ellenőrizd a charton is!" />
          <SourceDot source="CoinGecko (árváltozás) · Bybit (RSI, funding, L/S) · Binance (funding)" meta={mergeMeta(marketsMeta, rsiMeta, derivMeta)} />
        </div>
        <div className="strat-subtitle">Kezdőknek és haladóknak — mely feltételek teljesülnek most?</div>
      </div>

      {(tickersFailed || failed > 0) && <DataSourceDown onRetry={retry} />}
      <div className="strat-summary">
        <div className="strat-sum-card strat-sum-green">
          <div className="strat-sum-val">{activeCount}</div>
          <div className="strat-sum-label">Teljesül</div>
        </div>
        <div className="strat-sum-card strat-sum-yellow">
          <div className="strat-sum-val">{watchCount}</div>
          <div className="strat-sum-label">Részben</div>
        </div>
        <div className="strat-sum-card strat-sum-gray">
          <div className="strat-sum-val">{STRATEGIES.length - activeCount - watchCount}</div>
          <div className="strat-sum-label">Nem teljesül / nincs adat</div>
        </div>
        <div className="strat-disclaimer">
          ⚠ Ez nem pénzügyi tanácsadás. Minden döntés előtt végezz saját elemzést.
        </div>
      </div>

      <div className="strat-section-title">📈 Spot Stratégiák — Alacsony kockázat, kezdőknek is</div>
      <div className="strat-grid">
        {spotStrategies.map(s => (
          <StrategyCard key={s.id} strategy={s} signal={signals[s.id]} />
        ))}
      </div>

      <div className="strat-section-title">🤖 Grid Bot Stratégiák — Automatikus kereskedés</div>
      <div className="strat-grid">
        {gridStrategies.map(s => (
          <StrategyCard key={s.id} strategy={s} signal={signals[s.id]} />
        ))}
      </div>

      <div className="strat-section-title">⚡ Futures Stratégiák — Haladóknak</div>
      <div className="strat-grid">
        {futuresStrategies.map(s => (
          <StrategyCard key={s.id} strategy={s} signal={signals[s.id]} />
        ))}
      </div>

    </div>
  );
}
